/**
 * Reducer per il PurchaseModal del Marketplace.
 * Consolida lo stato precedentemente sparso su ~15 useState.
 *
 * Stage progression:
 *   choice (intent) → info → (flexible_price) → property → property_docs →
 *       (qty_visit) → (requirements) → quote | payment_choice → purchase → success | share_link
 *   ("qty_visit": scelta sopralluogo sì/no + quantità, solo se applicabili)
 */

export const EMPTY_FORM = {
  property_id: "",
  is_shared_with_client: false,
  client_name: "",
  client_email: "",
  client_markup: 0,
  // Admin on-behalf (non condivisa): prezzo servizio modificato a mano (null = calcolato).
  professional_price_override: null,
  notes: "",
  priority: "normal",
  // Iter72: quantità (default 1). Il selettore in PurchaseModal viene mostrato
  // solo se `service.allow_multiple_quantity` è True.
  quantity: 1,
};

export const EMPTY_QUOTE_FORM = { price_excl_vat: 0, vat_rate: 22, notes: "" };

export const EMPTY_NEW_PROP_FORM = {
  address: "",
  city: "",
  province: "",
  zip_code: "",
  // Iter209cl — Nuovo cliente/proprietario tipizzato.
  // Iter209cr — Ora la scelta è OBBLIGATORIA: default vuoto, l'utente
  // deve selezionare esplicitamente Privato o P.IVA prima di vedere
  // Nome/Cognome/Ragione sociale.
  owner_type: "", // "privato" | "azienda"
  owner_first_name: "",
  owner_last_name: "",
  owner_phone: "",
  owner_email: "",
  // Dati azienda (usati solo quando owner_type==="azienda")
  owner_company_name: "",
  owner_vat_number: "",
  owner_sdi_pec: "",
  // Iter209dp — Codice Fiscale aziendale opzionale (può differire dalla P.IVA
  // per enti non commerciali, ONLUS, associazioni).
  owner_company_fiscal_code: "",
  // Iter209cs — campo singolo "Indirizzo" alimentato da `fullAddress`
  // di ATOKA. I vecchi campi split restano per retro-compatibilità con
  // backend/UI altrove ma non sono più esposti in questo form.
  owner_company_address: "",
  owner_company_street: "",
  owner_company_street_number: "",
  owner_company_city: "",
  owner_company_province: "",
  owner_company_zip: "",
};

const currentMonth = () => {
  const d = new Date();
  return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

export const initialPurchaseState = {
  // Iter209e3 — "choice" è lo stage iniziale: il pro sceglie subito se
  // creare un preventivo o acquistare (`intent`), poi info → property → ...
  stage: "choice",
  intent: null, // "quote" | "purchase"
  // Iter209ep — file selezionati nello stage opzionale "property_docs".
  property_docs_files: [],
  form: { ...EMPTY_FORM },
  quoteForm: { ...EMPTY_QUOTE_FORM },
  generatingQuote: false,
  slotMonth: currentMonth(),
  availability: null,
  chosenSlot: null,
  selectedDate: null,
  visitOnFirstStep: false,
  chainedChoices: {},
  chainedServicesDetails: [],
  subMarginPct: 0,
  newPropForm: { ...EMPTY_NEW_PROP_FORM },
  creatingProp: false,
  createMode: false,
  propSearch: "",
  shareLinkInfo: null,
  // Snapshot info sul servizio appena acquistato (nome, pratica creata) — usato dalla
  // schermata di successo per mostrare un feedback ricco all'utente.
  successInfo: null,
};

export function purchaseReducer(state, action) {
  switch (action.type) {
    case "RESET":
      return { ...initialPurchaseState, slotMonth: currentMonth() };

    case "INIT_FOR_SERVICE": {
      // Chiamato quando l'utente apre il modal per un servizio.
      // action.service, action.chained (array), action.overrides (opzionale)
      const overrides = action.overrides || {};
      return {
        ...initialPurchaseState,
        slotMonth: currentMonth(),
        stage: overrides.stage || "choice",
        intent: overrides.intent || (overrides.stage === "quote" ? "quote" : overrides.stage ? "purchase" : null),
        form: { ...EMPTY_FORM, ...(overrides.form || {}) },
        quoteForm: {
          price_excl_vat: action.service?.professional_price || 0,
          vat_rate: 22,
          notes: "",
        },
        chainedServicesDetails: action.chained || [],
        chainedChoices: {},
      };
    }

    case "SET_STAGE":
      return { ...state, stage: action.stage };

    case "PATCH_FORM":
      return { ...state, form: { ...state.form, ...action.patch } };

    case "PATCH_QUOTE_FORM":
      return { ...state, quoteForm: { ...state.quoteForm, ...action.patch } };

    case "PATCH_NEW_PROP_FORM":
      return { ...state, newPropForm: { ...state.newPropForm, ...action.patch } };

    case "RESET_NEW_PROP_FORM":
      return { ...state, newPropForm: { ...EMPTY_NEW_PROP_FORM } };

    case "SET_CHAINED_CHOICE":
      return {
        ...state,
        chainedChoices: { ...state.chainedChoices, [action.id]: action.choice },
      };

    case "SET_FIELD":
      // Setter generico per campi top-level (creatingProp, createMode, propSearch,
      // subMarginPct, generatingQuote, availability, visitOnFirstStep, chosenSlot,
      // selectedDate, slotMonth, shareLinkInfo).
      return { ...state, [action.field]: action.value };

    case "PICK_MONTH":
      // Cambio mese calendario: azzera anche selectedDate/chosenSlot.
      return { ...state, slotMonth: action.month, selectedDate: null, chosenSlot: null };

    case "PICK_DATE":
      return { ...state, selectedDate: action.date, chosenSlot: null };

    case "CLEAR_SHARE_MODE":
      // Torna a payment_choice azzerando i dati cliente E lo slot eventualmente
      // selezionato mentre si era in modalità plafond. Il cliente sceglierà
      // il proprio slot autonomamente dal link pubblico di pagamento.
      return {
        ...state,
        stage: "payment_choice",
        selectedDate: null,
        chosenSlot: null,
        form: {
          ...state.form,
          is_shared_with_client: false,
          client_name: "",
          client_email: "",
          client_markup: 0,
        },
      };

    case "PURCHASE_SUCCESS":
      // Passa alla schermata di successo dopo una prenotazione plafond riuscita.
      // action.info: { practice_id, service_name, payment_method }
      return {
        ...state,
        stage: "success",
        successInfo: action.info || null,
      };

    default:
      return state;
  }
}
